'use client'

/**
 * TradeHeaderContainer - Transparent header for trade program page
 *
 * - Transparent on mobile only (below md/768px)
 * - Opaque (white) on tablet and desktop (md and above)
 * - Becomes opaque when scrolled past hero or when menu/search opens
 *
 * Uses inline Tailwind classes and data attributes for icon styling.
 */

import { useState, useEffect } from 'react'
import { useAppContext } from '@/app/_lib/_context'

const TradeHeaderContainer = ({ children }) => {
    const [collapsed, setCollapsed] = useState(false)
    const [opaque, setOpaque] = useState(false)
    const { mobileMenuOpen, activeSearch } = useAppContext()

    useEffect(() => {
        const handleScroll = () => {
            const scrollY = window.scrollY
            const isDesktop = window.innerWidth >= 768 // md breakpoint

            if (isDesktop) {
                // On desktop, never use collapsed/opaque (header is always white)
                if (collapsed) setCollapsed(false)
                if (opaque) setOpaque(false)
            } else {
                // On mobile, collapse almost immediately (10px threshold)
                const threshold = 10
                const isActive = scrollY > threshold
                setCollapsed(isActive)
                setOpaque(isActive)
            }
        }

        window.addEventListener('scroll', handleScroll, { passive: true })
        window.addEventListener('resize', handleScroll, { passive: true })
        return () => {
            window.removeEventListener('scroll', handleScroll)
            window.removeEventListener('resize', handleScroll)
        }
    }, [collapsed])

    // Sync collapsed state to body class for components outside header
    useEffect(() => {
        if (collapsed) {
            document.body.classList.add('header-collapsed')
        } else {
            document.body.classList.remove('header-collapsed')
        }
    }, [collapsed])

    // Determine if header should be opaque
    const isOpaque = opaque || mobileMenuOpen || activeSearch

    // Build class list - use min-h instead of h so header grows to fit content
    const baseClasses = 'group/header fixed top-0 left-0 w-full z-50 min-h-[var(--header-height)] flex flex-col transition-all duration-200 ease-out text-center md:relative md:border-b md:border-transparent'
    const collapsedClasses = collapsed ? 'collapsed min-h-[var(--header-height-collapsed)] md:min-h-[var(--header-height)] border-b border-gray-8' : ''
    const opaqueClasses = isOpaque ? 'bg-white! border-b border-gray-8' : 'bg-transparent md:bg-white/90'

    // Use CSS class for icon colors (added to header.css for trade-header)
    const headerClass = isOpaque ? 'trade-header trade-header-opaque' : 'trade-header'

    return (
        <header className={`${headerClass} ${baseClasses} ${collapsedClasses} ${opaqueClasses}`}>
            {children}
        </header>
    )
}

export default TradeHeaderContainer
